import React, { useContext, useEffect } from 'react';
import { FormContext } from 'contexts/FormContext';
import { useFormikContext } from 'formik';
import { get } from 'lodash';
import { ApplicationName } from 'components/CASAForm';
import { ArnApplicationDvsMethodFormData } from 'types/forms/arnApplicationTypes';
import { STATES, STATE_AND_TERRITORY_CODE } from 'arnConstants';
import {
  AuTextInput,
  AuDateInput,
  AuControlInput,
  AuCallout,
  AuFormItemWrapper,
  AuSelect,
  AuDirectionLink,
  AuErrorText
} from 'components/DTA';

const AustralianBirthCertificateDetails: React.FC = () => {
  const { handleBack, setFormAlert, openStepById } = useContext(FormContext);
  const { values } = useFormikContext<ArnApplicationDvsMethodFormData>();
  const stateOrTerritory = values?.auBirthCertificateDetails?.stateOrTerritory;
  const { errors, setFieldValue, getFieldMeta } = useFormikContext();
  const declarationFieldError = get(errors, 'auBirthCertificateDetails.declaration');

  useEffect(() => {
    setFieldValue('auBirthCertificateDetails.isManualUpload', false);
  }, []);

  useEffect(() => {
    setFieldValue('auBirthCertificateDetails.dateOfRegistration', '');
    setFieldValue('auBirthCertificateDetails.registrationNumber', '');
  }, [stateOrTerritory]);

  const stateRegistrationDescription = () => {
    switch (stateOrTerritory) {
      case STATE_AND_TERRITORY_CODE.ACT:
        return (
          <ul>
            <li>Most ACT birth certificates will have the Registration number in the last section of the certificate under &quot;Registration&quot;</li>
            <li>It may also appear in a box on the top right of the certificate</li>
          </ul>);
      case STATE_AND_TERRITORY_CODE.NSW:
        return (
          <ul>
            <li>Most NSW birth certificates will have the Registration number in a box on the top right of the certificate</li>
            <li>It may also be labelled as &apos;No. in Register&apos; or simply &apos;number&apos; next to &apos;Particulars of registration&apos;</li>
          </ul>);
      case STATE_AND_TERRITORY_CODE.NT:
        return (
          <ul>
            <li>Most Northern Territory birth certificates will have the Registration number in the last section of the certificate under &quot;Registration&quot;</li>
            <li>It may appear in a box on the top right of the certificate, or next to &apos;Copy of entry no.&apos; or &apos;Number in register&apos;</li>
          </ul>);
      case STATE_AND_TERRITORY_CODE.SA:
        return (
          <ul>
            <li>Most South Australian birth certificates will have the Registration number in the last section of the certificate under &quot;Registration&quot;</li>
            <li>Older birth certificates may have the Registration number on the top right or bottom right of the certificate</li>
          </ul>);
      case STATE_AND_TERRITORY_CODE.VIC:
        return (
          <ul>
            <li>Most Victorian birth certificates will have the Registration number in a box on the top right of the certificate</li>
            <li>It may also be labelled as &apos;No.&apos; on older certificates</li>
          </ul>);
      case STATE_AND_TERRITORY_CODE.WA:
        return (
          <ul>
            <li>Most Western Australian birth certificates will have the Registration number in a box on the top right of the certificate</li>
            <li>Older birth certificates may have the Registration number at the top of the certificate in first column or section of the certificate details</li>
          </ul>);
    }
  };

  const dvsValidationIndicator = getFieldMeta('auBirthCertificateDetails.dvsValidationIndicator').value;
  const isLocked = dvsValidationIndicator === 'Passed';
  const handleBackToIdentitySelection = () => {
    setFormAlert(null);
    openStepById('IdentityVerification');
  };

  return (
    <>
      <ApplicationName name='Australian birth certificate details' />
      <AuDirectionLink
        onClick={handleBackToIdentitySelection}
        text="Back"
        direction="left" />
      <AuTextInput
        name='auBirthCertificateDetails.familyName'
        label='Family name'
        hintText='Please enter your family name (surname) as written on your birth certificate'
        as='input'
        block
        disabled={isLocked} />
      <AuTextInput
        name='auBirthCertificateDetails.givenNames'
        label='Given names'
        hintText='Please enter all given names as written on your birth certificate, including any middle names'
        as='input'
        block
        disabled={isLocked} />
      <AuDateInput
        name='auBirthCertificateDetails.dateOfBirth'
        label='Date of birth'
        hintText='For example, 31 01 1990'
        disabled={isLocked} />
      <AuSelect
        name={'auBirthCertificateDetails.stateOrTerritory'}
        label='State or territory of birth'
        hintText='This is the state or territory where your birth was registered as indicated on your certificate'
        block
        disabled={isLocked}
        options={[
          {
            text: 'Please select',
            value: ''
          },
          ...STATES.map((state: { name: string; code: string; }) => {
            return {
              text: state.name,
              value: state.code
            };
          })
        ]} />
      {stateOrTerritory &&
        (stateOrTerritory === STATE_AND_TERRITORY_CODE.TAS || stateOrTerritory === STATE_AND_TERRITORY_CODE.QLD) &&
        <AuFormItemWrapper>
          <p>Please enter your Date of registration in numeric format; for example 14 02 1990. This can be found in the
            {stateOrTerritory === STATE_AND_TERRITORY_CODE.QLD && <span> &quot;Registrar&quot; </span>}
            {stateOrTerritory === STATE_AND_TERRITORY_CODE.TAS && <span> &quot;Date registered&quot; </span>}
            section towards the bottom of your birth certificate.
          </p>
          <AuDateInput
            name='auBirthCertificateDetails.dateOfRegistration'
            label=''
            disabled={isLocked}
            reset={true} />
        </AuFormItemWrapper>
      }
      {stateOrTerritory &&
        !(stateOrTerritory === STATE_AND_TERRITORY_CODE.TAS || stateOrTerritory === STATE_AND_TERRITORY_CODE.QLD) &&
        <>
          <AuTextInput
            name='auBirthCertificateDetails.registrationNumber'
            label='Registration number'
            hintText='Please enter your Registration number exactly as it appears on your birth certificate, including any slashes'
            as='input'
            block
            disabled={isLocked} />
          <AuFormItemWrapper>
            <p>Depending on your birth certificate type, your Registration number may appear in different places.</p>
            {stateRegistrationDescription()}
          </AuFormItemWrapper>
        </>
      }
      <AuFormItemWrapper>
        <AuCallout title='' style={{ paddingTop: '0' }}>
          <AuControlInput
            name='auBirthCertificateDetails.declaration'
            id='checkbox-declaration'
            label=''
            type='checkbox'
            disabled={isLocked}
            hintText='I confirm that I am authorised to provide the personal details presented and I consent to my information being checked with the document issuer or official record holder via third party systems for the purpose of confirming your identity'
          />
          {declarationFieldError && <AuErrorText text={declarationFieldError} />}
        </AuCallout>
      </AuFormItemWrapper>
    </>
  );
};

export default AustralianBirthCertificateDetails;